/* eslint-disable prefer-destructuring */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import CircularSlider from 'react-circular-slider-svg';

import { RowDataUpdatedEvent, ICellRendererParams } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import ContainerModal from 'wdna-components/lib/modals/generics/ContainerModal';
import { JarvisWidget } from 'wdna-components/lib';
import { messageError, messageWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { waste, sensor, RegisterInfoDO, StatisticsHashDO } from '../../../../sensors/requests';

interface Props {
  container: string;
  className: string;
  mainFilter: any;
}

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;
  showGraph: boolean;
  id?: number;
  data: any[];
  arrayData: any[];
  circleData?: StatisticsHashDO[];
  temperature?: number;
  fireAlarm?: boolean;
  batteryAlarm?: boolean;
  weightAlarm?: boolean;
  weightMax?: number;
  weightCur?: number;
}

export default class Summary extends React.Component<Props, State> {
  private mounted: boolean;
  private userIsURW: boolean;
  private buttons: TableButtonsConfig;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;
    this.userIsURW = false;

    this.buttons = {
      // toolbar
      // actions
      graph: true,
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,
      showGraph: false,
      data: [],
      arrayData: [],
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.userIsURW = hasLevel('role', ['ADM', 'URW']);

    this.load().catch(() => { /* pass */ });
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const filter: any = this.props.mainFilter;

      let companyId: number | undefined;
      if (filter.company !== '' && filter.company !== '0') {
        companyId = filter.company;
      }
      const [wasteTable] = await Promise.all([
        waste<RegisterInfoDO[]>('weights', undefined, { type: this.props.container, companyId }) || []
      ]);
      const { arrayData } = this.state;
      if (wasteTable.length !== 0) {
        for (let i: number = 0; i < wasteTable.length; i++) {
          const data: RegisterInfoDO = wasteTable[i];
          arrayData.push({
            id: data.sensorId,
            company: data.companyName,
            name: data.sensorName,
            description: data.sensorDescription,
            type: data.values.containerType,
            model: data.values.containerModel,
            size: data.values.containerSize,
            currentWeight: data.values.weightCur,
            currentTemp: data.values.temperatureCur,
          });
        }
      } else {
        messageWarning('Warning', 'No data for this subtype container');
      }
      this.setLocalState({ data: arrayData });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: false,
    });
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public numberCellRenderer = (params: ICellRendererParams): string => {
    let { value } = params;
    value = !isNaN(value) ? value && value.toFixed(2) : '';
    return value;
  };

  public onGraph = (value: any): void => {
    const rowId: number = value.id;
    this.getDashboardData(rowId).catch(() => { /* pass */ });
    this.dataAlerts(rowId).catch(() => { /* pass */ });
    this.setLocalState({
      id: rowId,
      update: window.getStr(),
      showGraph: true,
    });
  };

  public onDescription = (): string | undefined => {
    const { data } = this.state;
    for (let i: number = 0; i < data.length; i++) {
      if (data[i].id === this.state.id) {
        return data[i].description;
      }
    }
  };

  public onClose = (): void => {
    this.setLocalState({
      update: window.getStr(),
      showGraph: false,
    });
  };

  public sliderColor = (): string => {
    const subtype: string = this.props.container;
    if (subtype === 'SUW') return '#3A4749';
    if (subtype === 'FOR') return '#654321';
    if (subtype === 'PAP') return '#3895D3';
    if (subtype === 'PET') return '#F7DC10';
    if (subtype === 'GLA') return '#0B6623';
    if (subtype === 'PNG') return '#735902';
    if (subtype === 'CRK') return '#8C1F28';
    return '';
  };

  public percentage = (value: number, maxim: number): number => {
    const percent: number = (value / Math.max(maxim, 1)) * 100;
    return Math.max(Math.min(percent, 100), 0);
  };

  public getDashboardData = async (id: number): Promise<void> => {
    const { verified, active, arrFilterDate } = this.props.mainFilter;

    const { date1 } = arrFilterDate[0];
    const { date2 } = arrFilterDate[arrFilterDate.length - 1];
    const reqStatistics: Record<string, any> = {
      mode: 'CONTAINER', from: date1, to: date2, containerId: id
    };

    if (!this.userIsURW) {
      reqStatistics.verified = true;
      reqStatistics.active = true;
    } else {
      if (_.toNumber(verified) === 1) reqStatistics.verified = true;
      if (_.toNumber(active) === 1) reqStatistics.active = true;
    }
    const circleData: StatisticsHashDO[] = await waste<StatisticsHashDO[]>('statistics', undefined, reqStatistics) || [];
    this.setLocalState({
      circleData,
    }, () => {
      this.forceUpdate();
    });
  };

  public dataAlerts = async (id: number): Promise<void> => {
    const req2: Record<string, any> = { typeSensorId: [11], register: [5, 6], sensorId: id };
    const req3: Record<string, any> = { typeSensorId: [13], register: [9, 10], sensorId: id };
    const req4: Record<string, any> = { containerId: id };

    const [dataV1, dataV2, dataW] = await Promise.all([
      sensor<RegisterInfoDO[]>('register_lasts', undefined, req2) || [],
      sensor<RegisterInfoDO[]>('register_lasts', undefined, req3) || [],
      waste<RegisterInfoDO[]>('weights', undefined, req4) || []
    ]);

    const weightAlarm: boolean = dataW[0].values.weightAlarm === 1;
    const weightMax: number = dataW[0].values.weightMax;
    const weightCur: number = dataW[0].values.weightCur;

    let battery: number = 4;
    let temperature: number = 0;
    if (dataV1.length > 0) {
      battery = dataV1[0].values.d05;
      temperature = dataV1[0].values.d06.toFixed(1);
    }
    if (dataV2.length !== 0) {
      battery = dataV2[0].values.d09;
      temperature = dataV2[0].values.d10.toFixed(1);
    }

    // Upate alarm values
    const fireAlarm: boolean = (temperature > 49);
    const batteryAlarm: boolean = (battery < 3.3);

    this.setLocalState({
      temperature,
      fireAlarm,
      batteryAlarm,
      weightAlarm,
      weightMax,
      weightCur,
    }, () => {
      this.forceUpdate();
    });
  };

  public render(): JSX.Element {
    const { container, className } = this.props;
    const { loading, showGrid, showGraph, fireAlarm, batteryAlarm, weightAlarm, data, circleData, temperature } = this.state;
    const { data: cdata } = circleData?.[0] || {};

    let percent: number = 0;
    let style: React.CSSProperties = {};
    if (showGraph && cdata) {
      const curUnloadWeight: number = cdata[1].x || 0;
      const maxUnloadWeight: number = cdata[4].x || curUnloadWeight;
      percent = this.percentage(curUnloadWeight, maxUnloadWeight);

      const degrees: number = (percent > 0) ? 323 - (percent * 286) / 100 : 323;
      const radians: number = degrees * (Math.PI / 180);
      style = { left: `${29 + 58 * Math.cos(radians)}px`, top: `${18 - 58 * Math.sin(radians)}px`, textAlign: 'center', fontSize: '12px' };
    }

    const columnAlias: Record<string, string> = {
      id: 'ID',
      company: 'Company',
      name: 'Name',
      description: 'Description',
      type: 'Type',
      model: 'Model',
      size: 'Size',
      currentWeight: 'Weight',
      currentTemp: 'Temperature',

    };
    const columnsToHide: string[] = [
      'id',
    ];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'center' },
      company: { textAlign: 'left' },
      name: { textAlign: 'left' },
      code: { textAlign: 'left' },
      description: { textAlign: 'left' },
      model: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };
    const customColumnsDefs: Record<string, any> = {
      id: {
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        minWidth: 120,
      },
      currentWeight: { cellRenderer: this.numberCellRenderer },
      currentTemp: { cellRenderer: this.numberCellRenderer },
    };

    return (
      <div id="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          customCellStyles={columnStyles}
          hiddenCols={columnsToHide}
          buttons={this.buttons}
          customColumnsDefs={customColumnsDefs}
          loading={loading}
          rowDataUpdated={this.rowDataUpdated}
          onGraph={this.onGraph}
        />
        <ContainerModal
          fullScreenButton={false}
          show={showGraph}
          title="Info Sensors"
          onClose={this.onClose}
          modalClass="selector-modal-70"
          footerButtons={[{
            text: 'OK',
            className: 'btn btn-primary',
            onClick: this.onClose,
          }]}
        >
          <JarvisWidget
            editbutton={false}
            custombutton={false}
            togglebutton={false}
            fullscreenbutton={false}
            colorbutton={false}
            deletebutton={false}
            className="waste-main-dashboard"
          >
            <div className={`widget-body ${className} ${container}`}>
              <div className="container-alarms">
                {fireAlarm && (
                  <div className="alarm fire-alarm">
                    <div className="icon" title="FIRE-ALARM" />
                  </div>
                )}
                {weightAlarm && (
                  <div className="alarm weight-alarm">
                    <div className="icon" title="OVERWEIGHT-ALARM" />
                  </div>
                )}
                {batteryAlarm && (
                  <div className="alarm default-alarm">
                    <div className="icon" title="BATTERY-ALARM" />
                  </div>
                )}
              </div>
              <header className="container-header">
                <h2>
                  <span>{this.onDescription()}</span>
                  <small>{container}</small>
                </h2>
              </header>
              <div className="radial_graph">
                <div className="weight_values">
                  <div className="container_percentage" style={style}>
                    {percent.toFixed()}%
                  </div>
                  <div className="containers_total_weight">
                    {(cdata?.[1].x) || 0}
                  </div>
                  <div className="containers_day_weight">
                    {(cdata?.[3].x) || 0}<span>&nbsp;&nbsp;&nbsp;&nbsp;Kg/UNL</span>
                  </div>
                  <div className="container_unloads" style={{ paddingRight: '40px' }}>
                    {(cdata?.[2].x) || 0}
                  </div>
                </div>
                <div className="connection_signs">
                  <div className="wave1" />
                  <div className="wave2" />
                </div>
                <div className="circular_slider">
                  <CircularSlider
                    handle1={{
                      value: percent,
                      onChange: (): number => { return 100; },
                    }}
                    minValue={0}
                    maxValue={100}
                    size={160}
                    startAngle={-53}
                    endAngle={233}
                    handleSize={15}
                    arcColor={this.sliderColor()}
                  />
                </div>
              </div>

              <div className="containers_stats big_containers" style={{ top: '205px', left: '68px' }}>
                <div style={{ top: '33px', left: '7px', position: 'absolute', fontSize: '17px' }}>
                  <b>{temperature}ºC</b>
                </div>
                <div className="containers_by_temp">
                  <div className="thermometer">
                    <div className="celsius_symbol">&#176;C</div>
                    <div className="thermometer_mask" />

                    <div className="temps_blocks">
                      <div className="hot_temp_block" style={{ height: '40%' }} />
                      <div className="warm_temp_block" style={{ height: '25%' }} />
                      <div className="cold_temp_block" style={{ height: '35%' }} />
                    </div>
                    <div className="lower_temps_blocks">
                      <span className="mask">
                        <span className="bars_group">
                          <div className="hot_temp_block" style={{ height: '40%' }} />
                          <div className="warm_temp_block" style={{ height: '25%' }} />
                          <div className="cold_temp_block" style={{ height: '35%' }} />
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </JarvisWidget>
        </ContainerModal>
      </div>
    );
  }
}
